import type { ColumnDef } from '@tanstack/vue-table'
import { Member } from '@/types/Member'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { h } from 'vue'
import { active, disabled } from '@/data/userdata'
import DataTableColumnHeader from '@/components/DataTableColumnHeader.vue'
import DataTableRowActions from '@/components/DataTableRowActions.vue'
import DataTableTruncateText from '@/components/DataTableTruncateText.vue'

export const columns: ColumnDef<Member>[] = [
	{
		id: 'select',
		header: ({ table }) => h(Checkbox, {
			checked: table.getIsAllPageRowsSelected() ||
				(table.getIsSomePageRowsSelected() && 'indeterminate'),
			'onUpdate:checked': (value: boolean) => table.toggleAllPageRowsSelected(!!value),
			ariaLabel: 'Select all',
			class: 'translate-y-0.5'
		}),
		cell: ({ row }) => h(Checkbox, {
			checked: row.getIsSelected(),
			'onUpdate:checked': (value: boolean) => row.toggleSelected(!!value),
			ariaLabel: 'Select row',
			class: 'translate-y-0.5'
		}),
		enableSorting: false,
		enableHiding: false,
	},
	{
		accessorKey: 'email',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Email' }),
		cell: ({ row }) => h(DataTableTruncateText, { text: row.getValue('email') as string, maxLength: 24 }),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'firstname',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'First Name' }),
		cell: ({ row }) => h('div', {}, row.getValue('firstname')),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'lastname',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Last Name' }),
		cell: ({ row }) => h('div', {}, row.getValue('lastname')),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'user',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Username' }),
		cell: ({ row }) => h(DataTableTruncateText, { text: row.getValue('user') as string, maxLength: 14 }),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'active',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Active' }),
		cell: ({ row }) => {
			const act = active.find(
				active => active.value === row.getValue('active'),
			)
			if (!act) return null
			return h('div', { class: 'flex w-[100px] items-center' }, [
				act.icon && h(act.icon, { class: 'mr-2 h-4 w-4 text-muted-foreground' }),
			])
		},
		filterFn: (row, id, value) => {
			return value.includes(row.getValue(id))
		},
	},
	{
		accessorKey: 'disabled',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Disabled' }),
		cell: ({ row }) => {
			const disable = disabled.find(
				disable => disable.value === row.getValue('disabled'),
			)
			if (!disable) return null
			return h('div', { class: 'flex items-center' }, [
				disable.icon && h(disable.icon, { class: 'mr-2 h-4 w-4 text-muted-foreground' }),
			])
		},
		filterFn: (row, id, value) => {
			return value.includes(row.getValue(id))
		},
	},
	{
		accessorKey: 'city',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'City' }),
		cell: ({ row }) => h('div', { class: 'text-muted-foreground' }, row.getValue('city') || '—'),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'country',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Country' }),
		cell: ({ row }) => h('div', { class: 'text-muted-foreground' }, row.getValue('country') || '—'),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'created',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Created' }),
		cell: ({ row }) => {
			const val = row.getValue('created') as string
			if (!val) return h('span', { class: 'text-muted-foreground' }, '—')
			const d = new Date(val)
			return h('div', { class: 'text-muted-foreground text-xs whitespace-nowrap' }, d.toLocaleDateString())
		},
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'lastlogin',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Last Login' }),
		cell: ({ row }) => {
			const val = row.getValue('lastlogin') as string
			if (!val) return h('span', { class: 'text-muted-foreground' }, '—')
			const d = new Date(val)
			return h('div', { class: 'text-muted-foreground text-xs whitespace-nowrap' }, d.toLocaleDateString())
		},
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'publicfiles',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Public Files' }),
		cell: ({ row }) => h('div', { class: 'text-center tabular-nums' }, row.getValue('publicfiles') ?? 0),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'approvedfiles',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Approved' }),
		cell: ({ row }) => h('div', { class: 'text-center tabular-nums' }, row.getValue('approvedfiles') ?? 0),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'unmoderatedfiles',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Unmoderated' }),
		cell: ({ row }) => h('div', { class: 'text-center tabular-nums' }, row.getValue('unmoderatedfiles') ?? 0),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'deniedfiles',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Denied' }),
		cell: ({ row }) => h('div', { class: 'text-center tabular-nums' }, row.getValue('deniedfiles') ?? 0),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'followers',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Followers' }),
		cell: ({ row }) => h('div', { class: 'text-center tabular-nums' }, row.getValue('followers') ?? 0),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'following',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Following' }),
		cell: ({ row }) => h('div', { class: 'text-center tabular-nums' }, row.getValue('following') ?? 0),
		enableSorting: true,
		enableHiding: true,
	},
	{
		accessorKey: 'phone',
		header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Phone' }),
		cell: ({ row }) => h('div', { class: 'text-muted-foreground' }, row.getValue('phone') || '—'),
		enableSorting: false,
		enableHiding: true,
	},
	{
		id: 'actions',
		cell: ({ row }) => h(DataTableRowActions, { row }),
	},
]

// Default visibility — only show core columns, rest hidden by default
export const defaultColumnVisibility: Record<string, boolean> = {
	email: true,
	firstname: true,
	lastname: true,
	user: true,
	active: true,
	disabled: true,
	city: false,
	country: false,
	created: false,
	lastlogin: false,
	publicfiles: false,
	approvedfiles: false,
	unmoderatedfiles: false,
	deniedfiles: false,
	followers: false,
	following: false,
	phone: false,
}
